import { AutocompleteInput, SelectInput, FileInput, required } from 'react-admin';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { FormControlLabel, Switch, TextField, Tooltip } from '@mui/material';
import { CancelButtonStyled, SaveButtonStyled } from '@/components/toolbars/DefaultToolbar/styled';
import { OVERRIDE_DISABLED_TOOLTIP, path } from './constants';
import OverrideValidationDialog from './OverrideValidationDialog';
import ValidationResult from './ValidationResult';
import {
  CheckFields,
  Container,
  DropAction,
  DropHint,
  DropIcon,
  DropLead,
  DropNote,
  Field,
  FileList,
  FormToolbar,
  OptionalMark,
  OverrideRow,
  Pair,
  ProofActionEnd,
  ProofActions,
  ProofBlock,
  ProofIntro,
  ProofSubtitle,
  ProofTitle,
  ReportNotice,
  UploadCount,
} from './styled';
import { formatFileSize, reportFileName, uploadedAgo } from './adapters';
import OrderSection from './OrderSection';
import ProofFileCard from './ProofFileCard';
import { RequestOneOffCheckProps } from '../types';
import useController from './useController';

const FormContent = (props: RequestOneOffCheckProps) => {
  const {
    canOverrideReport,
    checkName,
    choices,
    disabled,
    disabledReason,
    fileInputOptions,
    fileUrl,
    onClearReport,
    onRemovePendingFile,
    pendingFile,
    reportCleared,
    storedReportUrl,
    uploading,
    handleOverrideChange,
    handleOverrideClose,
    handleOverrideConfirm,
    handleRequest,
    isUpdating,
    overrideChecked,
    showOverride,
    showOverrideModal,
    onOrderIdChange,
    onOrderRecordedChange,
    onReferenceIdChange,
    onVendorChange,
    orderId,
    orderLocked,
    orderRecorded,
    accountName,
    accountOptions,
    onAccountChange,
    referenceId,
    requestedAt,
    requestedBy,
    vendor,
    validateProof,
    validationFailures,
    validationRetry,
    validationSuccessLines,
  } = useController(props);
  const replacingReport = isUpdating && Boolean(props.record?.report_url) && !storedReportUrl;
  const storedUrl = reportCleared ? '' : props.record?.report_url || storedReportUrl || '';
  const pendingName = pendingFile?.name || (fileUrl ? reportFileName(fileUrl) : '');

  const saveButton = (label: string) => (
    <Tooltip disableHoverListener={!disabledReason} title={disabledReason || ''}>
      <span>
        <SaveButtonStyled
          alwaysEnable={!disabled}
          disabled={disabled}
          label={label}
          onClick={handleRequest}
          type="button"
        />
      </span>
    </Tooltip>
  );

  return (
    <>
      <Container>
        <OrderSection
          framed={isUpdating}
          leading={
            isUpdating ? undefined : (
              <AutocompleteInput
                choices={choices}
                label="Check Type"
                optionText="name"
                source={path('action')}
                validate={required()}
              />
            )
          }
          onClose={props.onClose}
          onOrderIdChange={onOrderIdChange}
          onOrderRecordedChange={onOrderRecordedChange}
          onRecordChange={props.onRecordChange}
          onReferenceIdChange={onReferenceIdChange}
          onVendorChange={onVendorChange}
          orderId={orderId}
          orderLocked={orderLocked}
          orderRecorded={orderRecorded}
          accountName={accountName}
          accountOptions={accountOptions}
          onAccountChange={onAccountChange}
          record={props.record}
          referenceId={referenceId}
          requestedAt={requestedAt}
          requestedBy={requestedBy}
          save={isUpdating ? saveButton('Save') : undefined}
          vendor={vendor}
          viewSource={
            orderLocked
              ? props.record
              : {
                  vendor,
                  vendor_id: orderId,
                  vendor_reference: referenceId,
                  vendor_specific_account: accountName,
                }
          }
          worker={props.worker}
        />

        {isUpdating && (
          <CheckFields>
            <legend>Check</legend>
            <Pair>
              <TextField disabled label="Check Type" size="small" value={checkName} />
              <SelectInput
                choices={choices}
                emptyText="Select"
                helperText="Leave this blank to keep Processing. Clear, consider, and fulfilled need a PDF."
                label={
                  <>
                    Status
                    <OptionalMark>Optional</OptionalMark>
                  </>
                }
                source={path('action')}
              />
            </Pair>
            <ProofBlock>
              <ProofIntro>
                <ProofTitle>Upload and attach file</ProofTitle>
                <ProofSubtitle>This PDF is saved as the report on this check.</ProofSubtitle>
              </ProofIntro>
              <Field>
                <FileInput
                  accept={{ 'application/pdf': ['.pdf'] }}
                  defaultValue={null}
                  disabled={uploading}
                  label={false}
                  onRemove={onRemovePendingFile}
                  options={fileInputOptions}
                  placeholder={
                    <DropHint>
                      <DropIcon>
                        <CloudUploadOutlinedIcon />
                      </DropIcon>
                      <DropLead>
                        <DropAction>Click to Upload</DropAction> or drag and drop
                      </DropLead>
                      <DropNote>PDF only</DropNote>
                      <DropNote>Required when the status is clear, consider, or fulfilled.</DropNote>
                    </DropHint>
                  }
                  source={path('file')}
                  validate={validateProof}
                />
                {(uploading || pendingName) && (
                  <FileList>
                    {uploading && <UploadCount>1 file uploading...</UploadCount>}
                    <ProofFileCard
                      detail={formatFileSize(pendingFile?.size)}
                      generatedName={!uploading && fileUrl ? reportFileName(fileUrl) : undefined}
                      href={!uploading ? fileUrl || undefined : undefined}
                      name={pendingName}
                      onRemove={uploading ? undefined : onRemovePendingFile}
                      removeLabel="Remove file"
                      status={uploading ? 'uploading' : 'complete'}
                    />
                    {storedUrl && (
                      <ProofFileCard
                        detail={uploadedAgo(props.record?.created_at)}
                        href={storedUrl}
                        name={reportFileName(storedUrl)}
                        onRemove={onClearReport}
                        removeLabel="Remove report"
                        status="stored"
                      />
                    )}
                  </FileList>
                )}
                {!uploading && !pendingName && storedUrl && (
                  <FileList>
                    <ProofFileCard
                      detail={uploadedAgo(props.record?.created_at)}
                      href={storedUrl}
                      name={reportFileName(storedUrl)}
                      onRemove={onClearReport}
                      removeLabel="Remove report"
                      status="stored"
                    />
                  </FileList>
                )}
              </Field>
              <ValidationResult
                failures={overrideChecked ? [] : validationFailures}
                retry={overrideChecked ? null : validationRetry}
                successLines={validationSuccessLines}
              />
              <ProofActions>
                <OverrideRow>
                  <Tooltip title={OVERRIDE_DISABLED_TOOLTIP} disableHoverListener={canOverrideReport}>
                    <span>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={overrideChecked}
                            disabled={!canOverrideReport}
                            onChange={(_, checked) => handleOverrideChange(checked)}
                            inputProps={{ 'aria-label': 'Override validation' }}
                          />
                        }
                        label="Override validation"
                        sx={{ marginLeft: 0, marginRight: 0 }}
                      />
                    </span>
                  </Tooltip>
                </OverrideRow>

                <ProofActionEnd>
                  {replacingReport && <ReportNotice>This report will be overridden.</ReportNotice>}
                </ProofActionEnd>
              </ProofActions>
            </ProofBlock>
          </CheckFields>
        )}

        <FormToolbar>
          {/* Order fields live outside react-hook-form, so form dirtiness cannot decide whether Save is enabled. */}
          {saveButton(isUpdating ? 'Save' : 'Request')}
          <CancelButtonStyled onClick={props.onClose}>Cancel</CancelButtonStyled>
        </FormToolbar>
      </Container>

      {showOverride && (
        <OverrideValidationDialog
          open={showOverrideModal}
          onClose={handleOverrideClose}
          onSubmit={handleOverrideConfirm}
        />
      )}
    </>
  );
};

export default FormContent;
